import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Hero shapes — staggered intro reveal for the image hero.
 *
 * Sequence:
 *   1. Left shape wipes in top → bottom
 *   2. Right shape wipes in bottom → top
 *   3. The image (or gallery) fades in
 *
 * Markup comes from the Hero block:
 *   <div class="block__hero__shapes" data-hero-shapes>
 *     <svg>
 *       <rect data-hero-shape="left">
 *       <rect data-hero-shape="right">
 *     </svg>
 *   </div>
 *
 * The bars are drawn straight and rotated with CSS (see blocks/hero.css), so a
 * clip-path on the inner .block__hero__shape-fill runs along each bar's OWN
 * axis. The shape therefore grows lengthwise from one end — which reads as a
 * clean directional wipe, unlike clipping an already-angled SVG.
 *
 * This module owns the image's intro opacity. hero-gallery.js handles the
 * ongoing crossfade and is told to hold off until this reveal finishes, so the
 * two never fight over the same property (see the data-hero-reveal-done flag).
 */
function initHeroShapes() {
  const wipe = 0.6;      // Duration of each shape wipe
  const overlap = 0.2;   // How early the second shape starts, in seconds
  const fade = 0.5;      // Copy / image fade duration
  // Head start given to the H1, whose SplitText reveal (utilities/animations.js)
  // fires on load and runs ~0.8s. This keeps the order H1 → copy+shapes → image.
  const headingLead = 0.35;

  document.querySelectorAll("[data-hero-shapes]").forEach((root) => {
    // Guard against double-init if called twice on the same DOM.
    if (root.hasAttribute("data-hero-shapes-initialized")) return;
    root.setAttribute("data-hero-shapes-initialized", "");

    // The clip runs on the UNROTATED wrapper, not the rotated bar inside it.
    const left = root.querySelector('[data-hero-shape="left"]');
    const right = root.querySelector('[data-hero-shape="right"]');

    // The media wrapper holds both the shapes and the image, so the image is a
    // sibling lookup from here rather than a descendant.
    const media = root.closest(".block__hero__media");
    const image = media?.querySelector(
      ".block__hero__gallery, .block__hero__image",
    );

    // Body copy and buttons, which fade alongside the shapes. The H1 is NOT
    // included: it has its own SplitText line reveal in utilities/animations.js
    // that fires on load, and animating opacity here would fight it.
    const block = root.closest(".block__hero");
    const copy = block
      ? Array.from(
          block.querySelectorAll(
            ".block__hero__content > :not(.block__hero__heading)",
          ),
        )
      : [];

    // Hand over from the CSS pre-JS state now that we control the animation.
    // The flag goes on the BLOCK, not the shapes element, because it also gates
    // the copy/buttons, which live outside .block__hero__shapes.
    root.classList.add("is-ready");
    block?.classList.add("is-reveal-ready");

    const markDone = () => {
      media?.setAttribute("data-hero-reveal-done", "");
      // hero-gallery.js waits on this before starting its crossfade loop.
      document.dispatchEvent(
        new CustomEvent("hero:revealed", { detail: { media } }),
      );
    };

    // Reduced motion: show everything at once, no wipes.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.set([left, right].filter(Boolean), { opacity: 1, clipPath: "none" });
      if (copy.length) gsap.set(copy, { autoAlpha: 1, y: 0 });
      if (image) gsap.set(image, { autoAlpha: 1 });
      markDone();
      return;
    }

    // Starting state. inset(top right bottom left) — the clipped-away edge is
    // 100%, so the shape is fully hidden; animating it to 0 uncovers it.
    // The wrapper is unrotated, so these are true screen directions:
    //   left  clipped from the BOTTOM → revealed downward (top → bottom)
    //   right clipped from the TOP    → revealed upward   (bottom → top)
    if (left) {
      gsap.set(left, { opacity: 1, clipPath: "inset(0% 0% 100% 0%)" });
    }
    if (right) {
      gsap.set(right, { opacity: 1, clipPath: "inset(100% 0% 0% 0%)" });
    }
    if (copy.length) gsap.set(copy, { autoAlpha: 0, y: 16 });
    if (image) gsap.set(image, { autoAlpha: 0 });

    // Sequence: H1 (its own SplitText reveal, already running) → copy/buttons
    // + shapes → image. `headingLead` holds the start back so the H1's line
    // reveal is underway before anything else moves.
    const tl = gsap.timeline({
      paused: true,
      delay: headingLead,
      onComplete: markDone,
    });

    if (left) {
      tl.to(left, {
        clipPath: "inset(0% 0% 0% 0%)",
        duration: wipe,
        ease: "power2.out",
      });
    }

    if (right) {
      tl.to(
        right,
        { clipPath: "inset(0% 0% 0% 0%)", duration: wipe, ease: "power2.out" },
        // Start just before the left wipe ends so the two feel connected
        // rather than strictly sequential.
        `>-${overlap}`,
      );
    }

    // Copy and buttons rise as the shapes wipe in — same beat, per the brief.
    // Position 0 puts them at the timeline start, alongside the first wipe.
    if (copy.length) {
      tl.to(
        copy,
        {
          autoAlpha: 1,
          y: 0,
          duration: fade,
          stagger: 0.08,
          ease: "power2.out",
        },
        0,
      );
    }

    if (image) {
      tl.to(image, { autoAlpha: 1, duration: fade, ease: "power1.out" });
    }

    // Play once the hero scrolls into view. `once` — this is an intro, not a
    // loop, so it must not replay on scroll back.
    ScrollTrigger.create({
      trigger: media || root,
      start: "top 85%",
      once: true,
      onEnter: () => tl.play(),
    });
  });
}

export { initHeroShapes };
